import {
  addDays,
  addWeeks,
  addYears,
  endOfDay,
  endOfWeek,
  isAfter,
  isBefore,
  startOfDay,
  startOfWeek,
  subDays,
  subWeeks,
  subYears
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

const MIN_START_DATE = startOfDay(subYears(new Date(), 150));
const MAX_END_DATE = endOfDay(addYears(new Date(), 150));

const clampToMinDate = (date) => {
  if (!date) return date;
  return isBefore(date, MIN_START_DATE) ? MIN_START_DATE : date;
};

const clampToMaxDate = (date) => {
  if (!date) return date;
  return isAfter(date, MAX_END_DATE) ? MAX_END_DATE : date;
};

const clampToBounds = (date) => clampToMaxDate(clampToMinDate(date));

const shiftDate = (date, unit, amount, forward) => {
  if (unit === 'days') return forward ? addDays(date, amount) : subDays(date, amount);
  if (unit === 'weeks') return forward ? addWeeks(date, amount) : subWeeks(date, amount);
  if (unit === 'months') {
    const d = new Date(date);
    d.setMonth(d.getMonth() + (forward ? amount : -amount));
    return d;
  }
  return forward ? addDays(date, amount) : subDays(date, amount);
};

export function getDateRange(unit, amount, timeZone, direction = 'past') {
  const isFuture = direction === 'future';
  const safeAmount = Math.max(0, Number(amount) || 0);
  const now = timeZone ? toZonedTime(new Date(), timeZone) : new Date();
  const shifted = shiftDate(now, unit, safeAmount, isFuture);

  if (isFuture) {
    return {
      startDate: startOfDay(now).toISOString(),
      endDate: clampToMaxDate(endOfDay(shifted)).toISOString()
    };
  }

  return {
    startDate: startOfDay(clampToBounds(shifted)).toISOString(),
    endDate: clampToMaxDate(endOfDay(now)).toISOString()
  };
}

export function getDateRangeByUnit(unit, amount, timeZone, direction = 'past') {
  const isFuture = direction === 'future';
  const safeAmount = Math.max(0, Number(amount) || 0);
  const now = timeZone ? toZonedTime(new Date(), timeZone) : new Date();
  const shifted = clampToBounds(shiftDate(now, unit, safeAmount, isFuture));

  if (unit === 'weeks') {
    const weekStart = startOfWeek(shifted);
    return {
      startDate: clampToMinDate(weekStart).toISOString(),
      endDate: clampToMaxDate(endOfDay(endOfWeek(shifted))).toISOString()
    };
  }

  return {
    startDate: startOfDay(shifted).toISOString(),
    endDate: clampToMaxDate(endOfDay(shifted)).toISOString()
  };
}

const PAST_OPTIONS = [
  { id: 'yesterday', label: 'Yesterday', unit: 'days', amount: 1 },
  { id: 'last-week', label: 'Last Week', unit: 'weeks', amount: 1 },
  { id: 'last-30-days', label: 'Last 30 Days', unit: 'days', amount: 30, range: true },
  { id: 'last-60-days', label: 'Last 60 Days', unit: 'days', amount: 60, range: true },
  { id: 'last-90-days', label: 'Last 90 Days', unit: 'days', amount: 90, range: true }
];

const FUTURE_OPTIONS = [
  { id: 'tomorrow', label: 'Tomorrow', unit: 'days', amount: 1, direction: 'future' },
  { id: 'next-week', label: 'Next Week', unit: 'weeks', amount: 1, direction: 'future' },
  {
    id: 'next-30-days',
    label: 'Next 30 Days',
    unit: 'days',
    amount: 30,
    range: true,
    direction: 'future'
  },
  {
    id: 'next-60-days',
    label: 'Next 60 Days',
    unit: 'days',
    amount: 60,
    range: true,
    direction: 'future'
  },
  {
    id: 'next-90-days',
    label: 'Next 90 Days',
    unit: 'days',
    amount: 90,
    range: true,
    direction: 'future'
  }
];

const buildOption = (opt, timeZone) => ({
  id: opt.id,
  label: opt.label,
  value: opt.range
    ? getDateRange(opt.unit, opt.amount, timeZone, opt.direction)
    : getDateRangeByUnit(opt.unit, opt.amount, timeZone, opt.direction)
});

const withBookends = (options, timeZone) => [
  { id: 'all-time', label: 'All Time', value: null },
  { id: 'today', label: 'Today', value: getDateRangeByUnit('days', 0, timeZone) },
  ...options,
  { id: 'custom-date-range', label: 'Custom date range', value: null }
];

export const getDateOptions = (timeZone) =>
  withBookends(
    PAST_OPTIONS.map((o) => buildOption(o, timeZone)),
    timeZone
  );

export const getFutureDateOptions = (timeZone) =>
  withBookends(
    FUTURE_OPTIONS.map((o) => buildOption(o, timeZone)),
    timeZone
  );

export const getAllDateOptions = (timeZone) =>
  withBookends(
    [...PAST_OPTIONS, ...FUTURE_OPTIONS].map((o) => buildOption(o, timeZone)),
    timeZone
  );

export const getOptionsByDirection = (direction, timeZone) => {
  if (direction === 'all') return getAllDateOptions(timeZone);
  if (direction === 'future') return getFutureDateOptions(timeZone);
  return getDateOptions(timeZone);
};

const buildSuggestion = (number, label1, labelN, unit, timezone, direction) => ({
  label: number === 1 ? label1 : `${labelN} ${number} ${unit}`,
  value: getDateRange(unit.toLowerCase(), number, timezone, direction)
});

const pastSuggestions = (number, timezone) => [
  buildSuggestion(number, 'Yesterday', 'Last', 'Days', timezone),
  buildSuggestion(number, 'Last Week', 'Last', 'Weeks', timezone),
  buildSuggestion(number, 'Last Month', 'Last', 'Months', timezone)
];

const futureSuggestions = (number, timezone) => [
  buildSuggestion(number, 'Tomorrow', 'Next', 'Days', timezone, 'future'),
  buildSuggestion(number, 'Next Week', 'Next', 'Weeks', timezone, 'future'),
  buildSuggestion(number, 'Next Month', 'Next', 'Months', timezone, 'future')
];

export const getSuggestions = (direction, number, timezone) => {
  if (direction === 'all')
    return [...pastSuggestions(number, timezone), ...futureSuggestions(number, timezone)];
  if (direction === 'future') return futureSuggestions(number, timezone);
  return pastSuggestions(number, timezone);
};
